import {DependencyList, useEffect, useRef} from 'react';

/**
 * `useEffect` that runs when `deps` change (and on mount with `runOnMount`), but not when a page kept alive
 * (`coreUtils/adapters/keepAlive`, React 19 `<Activity>`) is shown again: React runs every effect again then,
 * with the deps the page was left with. A change that happened while the page was hidden still runs it.
 *
 * For effects reacting to a change (e.g. sorting → URL and scroll), without cleanup.
 *
 * @param {Function} effect - Effect
 * @param {DependencyList} deps - Dependencies
 * @param {boolean} runOnMount - Run it on mount too (default: only on changes)
 */
const useChangeEffect = (effect: () => void, deps: DependencyList, runOnMount = false): void => {
	const lastDepsRef = useRef<DependencyList | null>(null);

	useEffect(() => {
		const lastDeps = lastDepsRef.current;

		lastDepsRef.current = deps;

		if (lastDeps ? deps.some((dep, index) => !Object.is(dep, lastDeps[index])) : runOnMount) {
			effect();
		}
		// The caller's deps, by design.
	}, deps);
};

export default useChangeEffect;
